import { inject, Injectable } from '@angular/core'
import { OrganizationsService } from '@ftr/api-core'
import { Organization } from '@ftr/contracts/api/organizations'
import { UserRole } from '@ftr/contracts/api/user'
import { Uuid } from '@ftr/contracts/type/shared'
import { ApiResult, RemoteData } from '@ftr/foundation'
import { Action, Selector, State, StateContext } from '@ngxs/store'
import { Observable, of, tap } from 'rxjs'
import {
  FetchOrganizationsAction,
  ResetOrganizationStateAction,
  SelectOrganizationAction,
} from './organization.actions'
import { OrganizationStateModel } from './organization.model'

export function defaultOrganizationState(): OrganizationStateModel {
  return {
    organizations: RemoteData.notAsked(),
    currentOrganizationId: undefined,
  }
}

@State<OrganizationStateModel>({
  name: 'organizationState',
  defaults: defaultOrganizationState(),
})
@Injectable()
export class OrganizationState {
  private readonly organizationsService = inject(OrganizationsService)

  @Selector()
  static organizations(state: OrganizationStateModel): RemoteData<Organization[]> {
    return state.organizations
  }

  @Selector()
  static currentOrganizationId(state: OrganizationStateModel): Uuid | undefined {
    return state.currentOrganizationId
  }

  @Selector()
  static currentUserRole(state: OrganizationStateModel): UserRole | undefined {
    return findCurrentOrganization(state)?.role
  }

  @Selector()
  static currentOrganization(state: OrganizationStateModel): Organization | undefined {
    return findCurrentOrganization(state)
  }

  @Action(FetchOrganizationsAction, { cancelUncompleted: true })
  fetchOrganizations(
    { getState, patchState }: StateContext<OrganizationStateModel>,
    { bypassCache }: FetchOrganizationsAction,
  ): ApiResult<Organization[]> {
    const { organizations } = getState()
    if (!bypassCache && (organizations.isSuccess() || organizations.isLoading())) {
      return of(organizations)
    }

    patchState({ organizations: RemoteData.loading() })

    return this.organizationsService.fetchForCurrentUser().pipe(tap(remote => patchState({ organizations: remote })))
  }

  @Action(SelectOrganizationAction)
  selectOrganization(
    ctx: StateContext<OrganizationStateModel>,
    { organizationId }: SelectOrganizationAction,
  ): Observable<unknown> {
    ctx.patchState({ currentOrganizationId: organizationId })

    if (!organizationId) {
      return of(undefined)
    }

    return ctx.dispatch(new FetchOrganizationsAction())
  }

  @Action(ResetOrganizationStateAction)
  resetOrderState({ setState }: StateContext<OrganizationStateModel>): void {
    setState(defaultOrganizationState())
  }
}

function findCurrentOrganization(organizationState: OrganizationStateModel): Organization | undefined {
  const { currentOrganizationId, organizations } = organizationState ?? {}
  return currentOrganizationId ? organizations.unwrapOr([]).find(({ id }) => id === currentOrganizationId) : undefined
}
